import _orderBy from 'lodash/orderBy';

import { getCategoriesPayloadFromLessonForm } from 'helpers/getLessonCategoryIdsFromApiLesson';
import { getLayerPropertyByKey } from 'helpers/getLayerPropertyByKey';
import { getTranslation } from 'helpers/getTranslation';

import { normalizeLayerPropertiesInput } from './normalizeLayerPropertiesInput';
import {
  normalizeFlowStartingPointsForForm,
  normalizePrototypeStartNodeForForm,
} from './normalizePrototypeMetaForForm';

const _getLayerOrderValue = (layer: any) =>
  getLayerPropertyByKey({ key: 'layerOrder', properties: layer.properties })
    ?.value ?? 0;

const _orderLayersToApi = (layers: any) =>
  _orderBy(layers, [_getLayerOrderValue], ['desc']);

const _formatProperty = (property: any) => {
  switch (property.key) {
    case 'innerRadius': {
      return Number(property.value);
    }
    case 'pointCount': {
      return Number(property.value);
    }
    case 'opacity': {
      return Number(property.value.toFixed(2));
    }
    default: {
      return property.value;
    }
  }
};

const _formatLayer = (layer: any): any => {
  const normalizedProperties = normalizeLayerPropertiesInput({
    properties: layer.properties,
    checkProperties: layer.checkProperties || [],
  });

  const apiProperties = normalizedProperties.reduce((obj: any, property: any) => {
    if (property.key === 'selection') {
      return obj;
    }

    obj[property.key] = _formatProperty(property);

    return obj;
  }, {});

  return {
    id: layer.id,
    name: layer.name,
    type: layer.type,
    checkProperties: normalizedProperties
      .filter((property: any) => property.check)
      .map((property: any) => property.key),
    properties: apiProperties,
    options: layer.options,
    children:
      layer.children && layer.children.length
        ? _orderLayersToApi(layer.children).map((childLayer: any): any =>
            _formatLayer(childLayer),
          )
        : [],
  };
};

const _formatComponentsLayer = (layer: any) => {
  const normalizedProperties = normalizeLayerPropertiesInput({
    properties: layer.properties,
  });

  return {
    id: layer.id,
    name: layer.name,
    type: layer.type,
    properties: normalizedProperties.reduce((obj: any, property: any) => {
      obj[property.key] = _formatProperty(property);

      return obj;
    }, {}),
    children:
      layer.children && layer.children.length
        ? layer.children.map((childLayer: any) =>
            _formatComponentsLayer(childLayer),
          )
        : [],
  };
};

const _formatTask = (task: any) => ({
  id: task.id,
  name: task.name,
  text: task.text,
  preview: task.preview,
  flowStartingPoints: normalizeFlowStartingPointsForForm(task.flowStartingPoints),
  prototypeStartNode: normalizePrototypeStartNodeForForm(task.prototypeStartNode),
  // theoryBlocks: task.theoryBlocks,
  hintsBlocks: task.hintsBlocks,
  materials: task.materials,
  files: task.files,
  checkStyles: task.checkStyles,
  layers: _orderLayersToApi(task.layers).map((layer: any) =>
    _formatLayer(layer),
  ),
});

export const convertDataToApi = (data: any) => {
  return {
    title: data.title || getTranslation('common.text.untitled'),
    free: data.free,
    fileId: data.fileId,
    filePageId: data.filePageId,
    designType: data.designType,
    goodToKnowBlocks: data.goodToKnowBlocks,
    canvasTheory: data.canvasTheory,
    tasks: data.tasks.map((task: any) => _formatTask(task)),
    description: data.description,
    goals: data.goals || [],
    percentToComplete: data.percentToComplete
      ? Number(data.percentToComplete)
      : null,
    resultRefPreview: data.resultRefPreview,
    initialState: data.initialState
      ? {
          preview: data.initialState.preview,
          flowStartingPoints: normalizeFlowStartingPointsForForm(
            data.initialState.flowStartingPoints,
          ),
          prototypeStartNode: normalizePrototypeStartNodeForForm(
            data.initialState.prototypeStartNode,
          ),
          componentsLayer: data.initialState.componentsLayer
            ? _formatComponentsLayer(data.initialState.componentsLayer)
            : null,
          layers: _orderLayersToApi(data.initialState.layers).map(
            (layer: any) => _formatLayer(layer),
          ),
        }
      : null,
    categories: getCategoriesPayloadFromLessonForm(data),
  };
};
